import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/lib/api";

/**
 * <BackgroundMediaLayer />
 *
 * Full-viewport background surface that sits BEHIND every page. Order of
 * resolution:
 *
 *   1. Try the current page's assignment from GET /api/appearance/settings.
 *   2. Fall back to the global default from the same endpoint.
 *   3. Fall back to a large, low-opacity brand watermark (the African
 *      Polistas logo, theme-appropriate variant) on the pure black canvas.
 *
 * Video behaviour follows the spec exactly:
 *   • muted, loop, autoplay, playsInline
 *   • lazy-loaded (`preload="metadata"`) so we don't burn mobile data
 *     before the user actually needs the frame
 *   • paused whenever document.visibilityState !== "visible" — critical
 *     for battery and data on mobile.
 *
 * The whole layer is portalled to document.body so it can escape any
 * `overflow: hidden` ancestor and keep painting the full viewport. It's
 * pointer-events: none so it never intercepts user input.
 *
 * PHASE-A NOTE: the /api/appearance/settings endpoint doesn't exist yet
 * (Phase D ships it). Until then we render the watermark placeholder
 * unconditionally — a graceful fallback that matches the spec's "empty
 * state" default. Once the endpoint lands, this component just starts
 * respecting its response with no other change.
 */
export function BackgroundMediaLayer({ pageKey = "global" }) {
  const { effective } = useTheme();
  const [settings, setSettings] = useState(null);          // resolved: {media_url, media_type, opacity} | null
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const videoRef = useRef(null);

  // Fetch resolved appearance settings for this page key. If the endpoint
  // isn't deployed yet (404) or the user can't reach it (401 during
  // auth-warmup) we silently fall through to the watermark placeholder.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/appearance/settings", { params: { page: pageKey } });
        if (!cancelled) setSettings(res.data || null);
      } catch (_e) {
        if (!cancelled) setSettings(null);
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [pageKey]);

  // Pause the background video when the tab loses focus. Prevents
  // battery/data drain on mobile devices per the spec.
  useEffect(() => {
    const handler = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.visibilityState === "visible") {
        // .play() returns a promise; ignore rejections (autoplay policy).
        v.play().catch(() => { /* noop */ });
      } else {
        v.pause();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  // Body flag so index.css can nudge sidebar/main to transparent surfaces
  // whenever the media layer is active (it's always active, but keeping
  // the hook lets us disable the layer via feature flag without editing
  // every page's CSS).
  useEffect(() => {
    document.body.setAttribute("data-ap-bg-media", "on");
    return () => document.body.removeAttribute("data-ap-bg-media");
  }, []);

  const media = settings?.enabled === false ? null : settings?.media_url ? settings : null;
  const opacity = media?.opacity != null ? media.opacity : 0.22;
  const opacityStyle = { "--ap-bg-media-opacity": opacity };

  const watermarkSrc = effective === "dark"
    ? "/brand/logo-white.png"
    : "/brand/logo-dark.png";

  const layer = (
    <div className="ap-bg-media" aria-hidden="true" data-testid="ap-bg-media-layer">
      {media?.media_type === "video" && (
        <video
          ref={videoRef}
          className="ap-bg-media__media"
          style={opacityStyle}
          src={media.media_url}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          data-testid="ap-bg-media-video"
        />
      )}
      {media?.media_type === "image" && (
        <img
          className="ap-bg-media__media"
          style={opacityStyle}
          src={media.media_url}
          alt=""
          data-testid="ap-bg-media-image"
        />
      )}
      {/* Watermark placeholder. shown whenever there's no media assigned
          for the current page and the global default is also empty. Also
          shown during the initial fetch so we never flash raw black. */}
      {!media && (
        <div className="ap-bg-media__watermark" data-testid="ap-bg-media-watermark">
          <img
            src={watermarkSrc}
            alt=""
            draggable={false}
          />
        </div>
      )}
      {/* Legibility scrim. always on, sits above the media but below
          every page's glass panels. Values baked into index.css so both
          themes match the spec exactly. */}
      <div className="ap-bg-media__scrim" />
    </div>
  );

  // Portal to <body> so we can guarantee we escape any `overflow: hidden`
  // ancestor and cover the whole viewport. `settingsLoaded` is checked
  // only so we can flip pause/play at the right time — the initial render
  // still shows the watermark so nothing ever flashes plain black.
  if (typeof document === "undefined") return null;
  void settingsLoaded;
  return createPortal(layer, document.body);
}

export default BackgroundMediaLayer;
